import { removeHiddenChars } from "../removeHiddenChars";

/**
 * Strip HTML tags from a string and return plain trimmed text.
 * Uses DOMParser so HTML entities (e.g. &amp;, &nbsp;) are decoded correctly.
 * Falls back to a regex strip in non-browser environments (e.g. tests with jsdom).
 */
export const stripHtml = (html) => {
    if (!html) return "";
    try {
        return new DOMParser().parseFromString(html, "text/html").body.textContent.trim();
    } catch {
        return html.replace(/<[^>]*>/g, "").trim();
    }
};
const COLOR_CLASSES = {
    EXCEEDS: "rating-blue",
    MEETS: "rating-green",
    APPROACHING: "rating-orange",
    "DOES NOT MEET": "rating-red",
    EXCLUDED: "rating-excluded"
};

export const RATING_DISPLAY_LABELS = {
    EXCEEDS: "Exceeds",
    MEETS: "Meets",
    APPROACHING: "Approaching",
    "DOES NOT MEET": "Does Not Meet",
    EXCLUDED: "Excluded"
};

export const RATING_OPTIONS = ["EXCEEDS", "MEETS", "APPROACHING", "DOES NOT MEET"];

export const getColorClass = (rating) => (rating ? COLOR_CLASSES[rating] : "");

const norm = (v) => (typeof v === "string" ? v.trim() : "");
export const upper = (v) => norm(v).toUpperCase();
export const sameName = (a, b) => upper(a) === upper(b) && a && b;

export const getScrubbedRating = (rating) => {
    return rating?.replaceAll("_", " ");
};

/** Scores parents should see. EXCLUDED, UNRATED, blank, and unknown values are not parent-facing. */
export const isParentFacingRating = (rating) => {
    if (rating == null || rating === "") return false;
    const scrubbed = getScrubbedRating(String(rating).trim());
    if (!scrubbed) return false;
    const upper = scrubbed.toUpperCase();
    if (upper === "EXCLUDED" || upper === "UNRATED") return false;
    return RATING_OPTIONS.includes(upper);
};

export const getFormattedHeaderAndTitle = (body, maxLength = 55) => {
    const matchShorthand = body.match(/\(([^)]+)\)\s*$/);
    const prefix = `${matchShorthand ? matchShorthand[1] : ""}`;

    const sentenceStart = body.split(".")[0].trim();
    const shortDescription =
        sentenceStart.length > maxLength ? sentenceStart.slice(0, maxLength).trim() + "…" : sentenceStart;

    const header = `${shortDescription}`;
    return { header, shortTitle: prefix };
};

export const extractStudentGradingProfileId = (key) => {
    return Number.parseInt(key.includes("-") ? key.split("-")[1] : key, 10);
};

export const byOrdinal = (a, b) => (a?.ordinal ?? 0) - (b?.ordinal ?? 0);

export const buildGroupInfoDto = (groupData, selectedEpr, selectedTemplate) => {
    const teacherHomeroomGroups = groupData
        .filter((g) => g.groupType === "GROUP" && !g.shareGroupId)
        .map((g) => ({ groupId: g.groupId }));

    const sharedGroupInfos = groupData
        .filter((g) => g.groupType === "SHARED" || !!g.shareGroupId)
        .map((g) => ({
            sharedGradingProfile: g.gradingProfileId,
            sharedGradingCriteria: g.sharedGradingCriteria,
            sharedGroupId: g.groupId
        }));

    const gradingCriteria = selectedEpr?.gradingCriteriaList.find(
        (c) => Number.parseInt(c.parentTemplate, 10) === selectedTemplate.templateId
    );

    return {
        teacherGradingProfile: selectedEpr ? selectedEpr.key : null,
        teacherGradingCriteria: selectedEpr ? gradingCriteria.key : null,
        teacherHomeroomGroups,
        sharedGroupInfos
    };
};

export const mapRatingSubmission = ({
    ratings,
    contentItems,
    existingRatingsData,
    selectedEpr,
    selectedTemplate,
    userDetails,
    groupData = []
}) => {
    const templateId = selectedTemplate.templateId;

    return Object.entries(ratings)
        .map(([key, rating]) => {
            const [studentKey, templateContentId] = key.split("_");
            const templateContentIdNum = Number(templateContentId);

            const studentId = studentKey.includes("-") ? Number(studentKey.split("-")[1]) : Number(studentKey);

            const groupId = studentKey.includes("-") ? Number(studentKey.split("-")[0]) : null;

            const group = groupData.find((g) => g.groupId === groupId);

            const gradingCriteriaId = group?.shareGroupId
                ? group.sharedGradingCriteria
                : selectedEpr?.gradingCriteriaList?.find((c) => Number.parseInt(c.parentTemplate, 10) === templateId)
                      ?.key;

            const existingStudent = existingRatingsData?.find((s) => s.key === studentKey);

            const existingRating = existingStudent?.contentRatings?.find(
                (r) => r.templateContentId === templateContentIdNum
            );

            const matchedItem = contentItems.find((c) => c.templateContentId === templateContentIdNum);
            if (!matchedItem) return null;

            return {
                key: existingRating?.key || null,
                gradingProfileStudentId: studentId,
                gradingCriteria: Number(gradingCriteriaId),
                templateContentId: templateContentIdNum,
                contentType: matchedItem.contentType,
                rating: rating === "DOES NOT MEET" ? "DOES_NOT_MEET" : rating,
                comment: existingRating?.comment ?? null,
                personalizedLearningPlan: false,
                lastRatedGuid: userDetails?.uid
            };
        })
        .filter((item) => item !== null && !(item.key === null && item.rating === null));
};

export const normalizeRatingForCompare = (rating) => (rating === "DOES NOT MEET" ? "DOES_NOT_MEET" : rating || null);

const normalizeCommentForCompare = (comment) => removeHiddenChars(String(comment ?? "").trim()) || null;

const findExistingDiffRow = (diff, existingDiffRatings) => {
    const list = existingDiffRatings ?? [];
    // match on db key first if exists
    if (diff.key != null) {
        const byKey = list.find((d) => String(d.key) === String(diff.key));
        if (byKey) return byKey;
    }
    // match on studentDiffId and contentId if no db key exists
    return list.find(
        (d) => String(d.studentDiffId) === String(diff.studentDiffId) && String(d.contentId) === String(diff.contentId)
    );
};

export const mapStudentRatingSubmission = ({
    student,
    selectedEpr,
    selectedTemplate,
    ratings,
    comments,
    contentItems,
    existingRatingsData = [],
    diffInputs,
    userGuid,
    providedGradingCriteriaId
}) => {
    const existingRatingsDataStudent = existingRatingsData?.[0]?.contentRatings ?? [];
    const existingDiffRatings = existingRatingsData?.[0]?.diffRatings ?? [];

    const contentRatings = Object.entries(ratings)
        .map(([templateContentId, rating]) => {
            const item = contentItems.find((c) => String(c.templateContentId) === String(templateContentId));
            if (!item) return null;

            const existingRating = existingRatingsDataStudent.find(
                (r) => String(r.templateContentId) === String(templateContentId)
            );

            const gradingCriteriaId =
                providedGradingCriteriaId ??
                selectedEpr?.gradingCriteriaList.find(
                    (c) => Number.parseInt(c.parentTemplate, 10) === selectedTemplate.templateId
                )?.key;

            const hasExistingKey = !!existingRating?.key;
            const normalizedRating = normalizeRatingForCompare(rating);
            if (!hasExistingKey && (normalizedRating === null || normalizedRating === "")) {
                return null;
            }

            const newComment = normalizeCommentForCompare(comments[templateContentId]);
            const ratingUnchanged =
                hasExistingKey &&
                String(normalizeRatingForCompare(existingRating.rating) ?? "") === String(normalizedRating ?? "");
            const commentUnchanged =
                hasExistingKey && normalizeCommentForCompare(existingRating.comment) === newComment;
            const contentUnchanged = ratingUnchanged && commentUnchanged;

            const lastRatedGuid = contentUnchanged ? (existingRating.lastRatedGuid ?? userGuid) : userGuid;

            return {
                key: hasExistingKey ? existingRating.key : null,
                gradingProfileStudentId: Number.parseInt(student.gradingProfileStudentId, 10),
                gradingCriteria: Number.parseInt(gradingCriteriaId, 10),
                templateContentId: Number.parseInt(templateContentId, 10),
                contentType: item?.contentType || "GLE",
                rating: normalizedRating,
                comment: newComment,
                personalizedLearningPlan: false,
                lastRatedGuid
            };
        })
        .filter(Boolean);

    // Build diffRatings array
    const diffRatings = diffInputs.map((diff) => {
        const normalizedDiffRating = normalizeRatingForCompare(diff.rating);
        const newDiffComment = normalizeCommentForCompare(diff.comment);
        const existingDiff = findExistingDiffRow(diff, existingDiffRatings);
        const diffUnchanged =
            existingDiff &&
            String(normalizeRatingForCompare(existingDiff.rating) ?? "") === String(normalizedDiffRating ?? "") &&
            normalizeCommentForCompare(existingDiff.comment) === newDiffComment;

        const lastRaterGuid = diffUnchanged ? (existingDiff.lastRaterGuid ?? userGuid) : userGuid;

        return {
            key: diff.key ?? null,
            studentDiffId: diff.studentDiffId,
            gradeToContentId: diff.gradeToContentId,
            gradingProfileStudentId: Number.parseInt(student.gradingProfileStudentId, 10),
            contentId: diff.contentId,
            contentType: diff.contentType,
            rating: normalizedDiffRating,
            comment: newDiffComment,
            personalizedLearningPlan: diff.personalizedLearningPlan ?? false,
            lastRaterGuid
        };
    });

    return {
        contentRatings,
        diffRatings
    };
};

export const normalizeRating = (backendValue) => {
    if (backendValue === "DOES_NOT_MEET") return "DOES NOT MEET";
    return backendValue;
};

export const getGradingCriteriaId = (student, groupData, selectedEpr, selectedTemplate, mode) => {
    const templateId = selectedTemplate.templateId;

    if (mode === "GROUPS") {
        // Prefer the group on this row's key (groupId-gpsId), not the first
        // membership — multi-group students were getting the wrong criteria.
        const key = student?.key != null ? String(student.key) : "";
        const groupIdFromKey = key.includes("-") ? Number(key.split("-")[0]) : null;
        const group =
            (groupIdFromKey != null &&
                !Number.isNaN(groupIdFromKey) &&
                groupData?.find((g) => Number(g.groupId) === groupIdFromKey)) ||
            groupData?.find((g) =>
                g.groupStudentContents.some(
                    (s) =>
                        s.gradingProfileStudentId.toString() === extractStudentGradingProfileId(student.key).toString()
                )
            );

        if (group?.shareGroupId) {
            return group.sharedGradingCriteria;
        }

        return selectedEpr?.gradingCriteriaList?.find((c) => Number.parseInt(c.parentTemplate, 10) === templateId)?.key;
    }
    return selectedEpr?.gradingCriteriaList?.find((c) => Number.parseInt(c.parentTemplate, 10) === templateId)?.key;
};

export const buildGroupFetchPayload = ({ selectedGroups, selectedEpr, selectedTemplate, teacherGuid }) => {
    if (!selectedTemplate) return null;

    const teacherGradingProfile = selectedEpr?.key ? Number.parseInt(selectedEpr?.key, 10) : null;
    const teacherGradingCriteria = selectedEpr?.gradingCriteriaList?.find(
        (criteria) => Number.parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
    );

    const payload = {
        teacherGuid,
        teacherGradingProfile,
        teacherGradingCriteria: selectedEpr ? Number.parseInt(teacherGradingCriteria?.key, 10) : null,
        teacherHomeroomGroups: [],
        sharedGroupInfos: []
    };

    selectedGroups.forEach((group) => {
        const [, idString] = group.value.split("-");
        const id = Number(idString);

        if (group.value.startsWith("homeroom-")) {
            payload.teacherHomeroomGroups.push({ groupId: id });
        } else if (group.value.startsWith("shared-")) {
            const meta = group.sharedMeta;

            payload.sharedGroupInfos.push({
                sharedGradingProfile: meta.sharedGradingProfile,
                sharedGradingCriteria: meta.sharedGradingCriteria ?? group.matchingCriteriaId,
                sharedGroupId: meta.sharedGroupId
            });
        }
    });

    return payload;
};
